/**
 * Cognito Hosted UI auth (authorization code + PKCE).
 * Tokens are stored in localStorage.
 */
import config from "../cognito-config";

const STORAGE_KEY = "team.cognito.tokens";
const PKCE_VERIFIER_KEY = "team.cognito.pkce_verifier";
const PROVIDER = "IDC";

function oauth() {
  return config.oauth || {};
}

function redirectUri() {
  const fallback = window.location.origin.replace(/\/$/, "");
  return (oauth().redirectSignIn || fallback).replace(/\/$/, "");
}

function logoutUri() {
  return (oauth().redirectSignOut || redirectUri()).replace(/\/$/, "");
}

function scopes() {
  return (oauth().scope || ["openid", "email", "profile"]).join(" ");
}

function authorizeUrl() {
  return `https://${oauth().domain}/oauth2/authorize`;
}

function tokenUrl() {
  return `https://${oauth().domain}/oauth2/token`;
}

function logoutUrl() {
  return `https://${oauth().domain}/logout`;
}

function b64url(bytes) {
  let s = "";
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i += 1) s += String.fromCharCode(arr[i]);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomVerifier(len = 64) {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return b64url(bytes);
}

async function challengeS256(verifier) {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return b64url(digest);
}

function parseJwt(token) {
  if (!token) return null;
  const part = token.split(".")[1];
  if (!part) return null;
  const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
  return JSON.parse(json);
}

function loadTokens() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveTokens(tokens) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

function clearTokens() {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
}

function tokensToSession(tokens) {
  if (!tokens?.id_token) return null;
  const idTokenPayload = parseJwt(tokens.id_token);
  if (!idTokenPayload) return null;
  return {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresAt: tokens.expires_at,
    idTokenPayload,
  };
}

async function exchangeCode(code, verifier) {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.userPoolWebClientId,
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  const res = await fetch(tokenUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error_description || data.error || `token exchange ${res.status}`);
  }
  const expiresAt = Date.now() + (Number(data.expires_in) || 3600) * 1000;
  const tokens = {
    id_token: data.id_token,
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    expires_at: expiresAt,
    token_type: data.token_type,
  };
  saveTokens(tokens);
  return tokensToSession(tokens);
}

async function refreshSession(tokens) {
  if (!tokens?.refresh_token) return null;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: config.userPoolWebClientId,
    refresh_token: tokens.refresh_token,
  });
  const res = await fetch(tokenUrl(), {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    clearTokens();
    return null;
  }
  const next = {
    id_token: data.id_token,
    access_token: data.access_token,
    refresh_token: data.refresh_token || tokens.refresh_token,
    expires_at: Date.now() + (Number(data.expires_in) || 3600) * 1000,
    token_type: data.token_type,
  };
  saveTokens(next);
  return tokensToSession(next);
}

function stripAuthParamsFromUrl() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has("code") && !url.searchParams.has("error")) return;
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  url.searchParams.delete("error");
  url.searchParams.delete("error_description");
  window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
}

/** Start Hosted UI login with Identity Center IdP (IDC). */
export async function signInWithIdc() {
  const verifier = randomVerifier();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  const challenge = await challengeS256(verifier);
  const params = new URLSearchParams({
    client_id: config.userPoolWebClientId,
    response_type: oauth().responseType || "code",
    scope: scopes(),
    redirect_uri: redirectUri(),
    identity_provider: PROVIDER,
    code_challenge_method: "S256",
    code_challenge: challenge,
  });
  window.location.assign(`${authorizeUrl()}?${params.toString()}`);
}

/** Clear local tokens and redirect through Cognito logout. */
export async function signOut() {
  clearTokens();
  const params = new URLSearchParams({
    client_id: config.userPoolWebClientId,
    logout_uri: logoutUri(),
  });
  window.location.assign(`${logoutUrl()}?${params.toString()}`);
}

/**
 * Handle OAuth redirect if present; otherwise load/refresh stored session.
 * Call once on app boot.
 */
export async function initAuth() {
  const url = new URL(window.location.href);
  const err = url.searchParams.get("error");
  if (err) {
    console.error("[cognitoAuth] Hosted UI error:", err, url.searchParams.get("error_description"));
    stripAuthParamsFromUrl();
    clearTokens();
    return null;
  }

  const code = url.searchParams.get("code");
  if (code) {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    stripAuthParamsFromUrl();
    if (!verifier) {
      console.error("[cognitoAuth] missing PKCE verifier");
      return null;
    }
    try {
      return await exchangeCode(code, verifier);
    } catch (e) {
      console.error("[cognitoAuth] code exchange failed", e);
      clearTokens();
      return null;
    }
  }

  return getSession();
}

/** Return a valid session, refreshing if within 60s of expiry. */
export async function getSession() {
  const tokens = loadTokens();
  if (!tokens?.id_token) return null;
  const skewMs = 60 * 1000;
  if (tokens.expires_at && Date.now() < tokens.expires_at - skewMs) {
    return tokensToSession(tokens);
  }
  return refreshSession(tokens);
}

/** ID token JWT string for API Authorization header. */
export async function getIdToken() {
  const session = await getSession();
  return session?.idToken || null;
}

export function getIdTokenPayloadSync() {
  const tokens = loadTokens();
  return tokens?.id_token ? parseJwt(tokens.id_token) : null;
}
